import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import TopBar from "@/components/TopBar";
import api, { extractError } from "@/api/client";
import useWebSocket from "@/hooks/useWebSocket";
import PushEnableBanner from "@/components/PushEnableBanner";
import { useAuth } from "@/contexts/AuthContext";
import { RefreshCw, Bike, ChefHat, PackageCheck, Home, X, UserCheck, MapPin, Phone, Radio, Store, UtensilsCrossed, Wallet, Check, BarChart3, Users, Trash2, TrendingUp, FileBarChart2, ArrowRightLeft, ChevronLeft, Zap, Receipt, Coins, Eye, Truck, DollarSign, Wallet2 } from "lucide-react";
import ReportsPanel from "@/components/ReportsPanel";
import AdminUsersModal from "@/components/AdminUsersModal";
import AdminApprovalsModal from "@/components/AdminApprovalsModal";
import AdminCardsModal from "@/components/AdminCardsModal";
import OrderReassignModal from "@/components/OrderReassignModal";
import PasswordResetRequestsModal from "@/components/PasswordResetRequestsModal";
import SupportContactsModal from "@/components/SupportContactsModal";
import DuplicatesModal from "@/components/DuplicatesModal";
import MergeLogModal from "@/components/MergeLogModal";
import DriverWalletsModal from "@/components/DriverWalletsModal";
import AdminSidebar from "@/components/AdminSidebar";
import AdminProfileModal from "@/components/AdminProfileModal";
import AdminTeamModal from "@/components/AdminTeamModal";
import CancelRequestsModal from "@/components/CancelRequestsModal";
import AdminBroadcastModal from "@/components/AdminBroadcastModal";
import AdminCashoutsModal from "@/components/AdminCashoutsModal";
import TwilioSettingsModal from "@/components/TwilioSettingsModal";
import AdminBetaTestersModal from "@/components/AdminBetaTestersModal";
import AdminBannersModal from "@/components/AdminBannersModal";
import AdminCategorySettingsModal from "@/components/AdminCategorySettingsModal";

const COLUMNS = [
  { key: "pending",   title: "طلب جديد",       color: "bg-yellow-50 border-yellow-200",  next: "preparing", nextLabel: "بدء التحضير", icon: ChefHat },
  { key: "preparing", title: "قيد التحضير",    color: "bg-blue-50 border-blue-200",  next: "ready",     nextLabel: "جاهز",         icon: PackageCheck },
  { key: "ready",     title: "جاهز للاستلام",  color: "bg-blue-50 border-blue-200",      next: null,        nextLabel: "تعيين سائق",  icon: Bike },
  { key: "assigned",  title: "مع السائق",      color: "bg-purple-50 border-purple-200",  next: null, icon: Bike },
  { key: "picked_up", title: "في الطريق",      color: "bg-primary-light border-blue-300", next: null, icon: MapPin },
  { key: "delivered", title: "تم التسليم",     color: "bg-green-50 border-green-200",    next: null, icon: Home },
];

// ────────── Reusable KPI card + section heading ──────────
// Ubary-style palette: pastel icon chip in the top-right (RTL), bold value
// centered under it, tiny label at the bottom. Section headers get a short
// underline accent to visually separate groups (اليوم / المنصة / …).
const TONE_STYLES = {
  amber:   { bg: "bg-amber-50",   text: "text-amber-600" },
  red:     { bg: "bg-red-50",     text: "text-red-500" },
  green:   { bg: "bg-emerald-50", text: "text-emerald-600" },
  blue:    { bg: "bg-sky-50",     text: "text-sky-600" },
  emerald: { bg: "bg-teal-50",    text: "text-teal-600" },
  yellow:  { bg: "bg-yellow-50",  text: "text-yellow-600" },
  pink:    { bg: "bg-pink-50",    text: "text-pink-600" },
  indigo:  { bg: "bg-indigo-50",  text: "text-indigo-600" },
};

function SectionHeader({ title }) {
  return (
    <div className="mb-3 mt-1 flex items-center">
      <div className="relative">
        <h2 className="text-sm font-black text-[var(--text-primary)]" style={{fontFamily:'Cairo'}}>{title}</h2>
        <span className="absolute -bottom-1 right-0 w-8 h-[3px] rounded-full bg-primary-brand"/>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, tone = "blue", label, value, testId, trailing = null, cardClass = "" }) {
  const t = TONE_STYLES[tone] || TONE_STYLES.blue;
  return (
    <div
      className={`relative bg-white rounded-2xl px-4 pt-3 pb-4 border border-[var(--border)] talabya-shadow-soft hover:shadow-md transition ${cardClass}`}
      data-testid={testId}
    >
      {/* Icon pinned to top-end (top-right in RTL) — matches Ubary reference. */}
      <div className={`w-9 h-9 rounded-xl ${t.bg} ${t.text} flex items-center justify-center mb-3 ms-auto`}>
        <Icon size={16}/>
      </div>
      <div className="font-black text-2xl leading-tight text-[var(--text-primary)]" style={{fontFamily:'Cairo'}}>{value}</div>
      <div className="text-[11px] font-bold text-[var(--text-secondary)] leading-snug mt-1">{label}</div>
      {trailing}
    </div>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [err, setErr] = useState("");
  const [showPricing, setShowPricing] = useState(false);
  const [showSales, setShowSales] = useState(false);
  const [showDrivers, setShowDrivers] = useState(false);
  const [showReports, setShowReports] = useState(false);
  const [showUsers, setShowUsers] = useState(false);
  const [showApprovals, setShowApprovals] = useState(false);
  const [showCards, setShowCards] = useState(false);
  const [showPwResets, setShowPwResets] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [showMergeLog, setShowMergeLog] = useState(false);
  const [showClearRevenue, setShowClearRevenue] = useState(false);
  const [clearBusy, setClearBusy] = useState(false);
  const [clearResult, setClearResult] = useState(null);
  const [platformRevenue, setPlatformRevenue] = useState(null);
  const [showDriverWallets, setShowDriverWallets] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showTeam, setShowTeam] = useState(false);
  const [showCancels, setShowCancels] = useState(false);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [showCashouts, setShowCashouts] = useState(false);
  const [showTwilio, setShowTwilio] = useState(false);
  const [showBeta, setShowBeta] = useState(false);
  const [showBanners, setShowBanners] = useState(false);
  const [showCatSettings, setShowCatSettings] = useState(false);
  const [pendingCashoutsCount, setPendingCashoutsCount] = useState(0);
  const [pendingCancelCount, setPendingCancelCount] = useState(0);
  const [pendingResetsCount, setPendingResetsCount] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const [o, d, pw, cr, pr, co] = await Promise.all([
        api.get("/admin/orders"),
        api.get("/admin/drivers"),
        api.get("/admin/password-reset-requests", { params: { status: "pending", limit: 1 } }).catch(() => ({ data: { pending_count: 0 } })),
        api.get("/admin/cancel-requests", { params: { status: "pending" } }).catch(() => ({ data: { count: 0 } })),
        api.get("/admin/platform-revenue", { params: { days: 7 } }).catch(() => ({ data: null })),
        api.get("/admin/cashouts", { params: { status: "pending" } }).catch(() => ({ data: { pending_count: 0 } })),
      ]);
      setOrders(o.data);
      setDrivers(d.data);
      setPendingResetsCount(pw.data?.pending_count || 0);
      setPendingCancelCount(cr.data?.count || 0);
      setPlatformRevenue(pr.data);
      setPendingCashoutsCount(co.data?.pending_count || 0);
    } catch (e) { setErr(extractError(e)); }
  }, []);

  const loadCashoutsCount = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/cashouts", { params: { status: "pending" } });
      setPendingCashoutsCount(data?.pending_count || 0);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 20000);
    return () => clearInterval(t);
  }, [refresh]);

  const { status: wsStatus } = useWebSocket({
    onMessage: (msg) => {
      if (msg.event?.startsWith("order.")) refresh();
      if (msg.event === "password_reset.requested") refresh();
    },
  });





  const stats = {
    active: orders.filter((o) => !["delivered", "cancelled"].includes(o.status)).length,
    today: orders.filter((o) => o.created_at?.startsWith(new Date().toISOString().slice(0,10))).length,
    revenue: orders.filter((o) => o.status === "delivered").reduce((s, o) => s + o.total, 0),
    drivers_online: drivers.filter((d) => d.status !== "offline").length,
  };

  return (
    <div className="min-h-screen bg-[var(--background)]">
      <TopBar />
      <PushEnableBanner/>
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6">

        {/* 2-column layout: main content (left) + sticky vertical sidebar (right on RTL) */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-6">
          {/* Main content */}
          <div className="min-w-0">
            {/* Page title */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-2xl bg-primary-brand text-white flex items-center justify-center talabya-shadow-soft">
                <BarChart3 size={20}/>
              </div>
              <div>
                <h1 className="text-2xl font-black leading-none" style={{fontFamily:'Cairo'}}>لوحة إدارة الطلبات</h1>
                <div className="text-xs text-[var(--text-secondary)] mt-1">نظرة عامة لحظية على العمليات</div>
              </div>
            </div>

            {/* ═════ Section: اليوم ═════ */}
            <SectionHeader title="اليوم"/>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="stats-today">
              <StatCard
                testId="stat-active"
                icon={Zap}
                tone="amber"
                label="طلبات نشطة الآن"
                value={stats.active}
              />
              <StatCard
                testId="stat-today-orders"
                icon={Receipt}
                tone="red"
                label="طلبات اليوم"
                value={stats.today}
              />
              <StatCard
                testId="stat-revenue-card"
                icon={Coins}
                tone="green"
                label="مبيعات اليوم (مكتملة)"
                value={`${stats.revenue.toFixed(2)} د.ل`}
                trailing={
                  <button
                    onClick={() => setShowClearRevenue(true)}
                    className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity w-7 h-7 rounded-lg bg-red-50 hover:bg-red-500 hover:text-white text-red-600 flex items-center justify-center"
                    title="مسح إيرادات مكتملة (حذف الطلبات المُسلَّمة)"
                    data-testid="btn-clear-revenue"
                  >
                    <Trash2 size={13}/>
                  </button>
                }
                cardClass="group"
              />
              <StatCard
                testId="stat-platform-revenue-card"
                icon={Eye}
                tone="blue"
                label={`إيرادات المنصة اليوم${(platformRevenue?.today?.deliveries || 0) ? ` · ${platformRevenue.today.deliveries} توصيلة` : ""}`}
                value={`${(platformRevenue?.today?.revenue || 0).toFixed(2)} د.ل`}
              />
            </div>

            {/* ═════ Section: المنصة ═════ */}
            <SectionHeader title="المنصة"/>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="stats-platform">
              <StatCard
                testId="stat-drivers-online"
                icon={Bike}
                tone="emerald"
                label="السائقون المتصلون"
                value={`${stats.drivers_online} / ${drivers.length}`}
              />
              <StatCard
                testId="stat-week-deliveries"
                icon={Truck}
                tone="yellow"
                label="توصيلات الأسبوع"
                value={platformRevenue?.week?.deliveries || 0}
              />
              <StatCard
                testId="stat-week-revenue"
                icon={TrendingUp}
                tone="pink"
                label="إيرادات المنصة (أسبوع)"
                value={`${(platformRevenue?.week?.revenue || 0).toFixed(2)} د.ل`}
              />
              <StatCard
                testId="stat-lifetime-revenue"
                icon={Wallet2}
                tone="indigo"
                label="إجمالي إيرادات المنصة"
                value={`${(platformRevenue?.lifetime?.revenue || 0).toFixed(2)} د.ل`}
              />
            </div>

            {/* Big call-to-action tile → Orders management */}
            <Link to="/admin/orders" data-testid="admin-cta-orders"
                  className="block group bg-white rounded-3xl p-6 talabya-shadow-soft border-2 border-transparent hover:border-primary-brand transition mb-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-primary-brand text-white flex items-center justify-center group-hover:scale-105 transition-transform">
                  <PackageCheck size={26}/>
                </div>
                <div className="flex-1">
                  <div className="font-black text-lg" style={{fontFamily:'Cairo'}}>إدارة الطلبات المباشرة</div>
                  <div className="text-xs text-[var(--text-secondary)] mt-1">افتح لوحة العمليات المفصّلة — Kanban + جدول + فلاتر</div>
                </div>
                <ChevronLeft className="text-primary-brand group-hover:-translate-x-1 transition-transform" size={22}/>
              </div>
            </Link>

            {/* Alerts strip */}
            {(pendingResetsCount > 0 || pendingCancelCount > 0) && (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6">
                <div className="font-black text-sm text-amber-800 mb-2" style={{fontFamily:'Cairo'}}>⚠️ تنبيهات تحتاج انتباهك</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {pendingResetsCount > 0 && (
                    <button onClick={() => setShowPwResets(true)}
                            className="bg-white rounded-xl px-3 py-2 flex items-center justify-between hover:bg-amber-100"
                            data-testid="alert-pwresets">
                      <span className="text-sm">طلبات استعادة كلمة مرور</span>
                      <span className="font-black text-red-500 bg-red-50 rounded-full px-2">{pendingResetsCount}</span>
                    </button>
                  )}
                  {pendingCancelCount > 0 && (
                    <button onClick={() => setShowCancels(true)}
                            className="bg-white rounded-xl px-3 py-2 flex items-center justify-between hover:bg-amber-100"
                            data-testid="alert-cancels">
                      <span className="text-sm">طلبات إلغاء بانتظار المراجعة</span>
                      <span className="font-black text-red-500 bg-red-50 rounded-full px-2">{pendingCancelCount}</span>
                    </button>
                  )}
                </div>
              </div>
            )}

            {err && <div className="bg-red-50 text-red-600 px-4 py-2 rounded-xl text-sm mb-3">{err}</div>}
          </div>

          {/* Sidebar (right on RTL) */}
          <AdminSidebar
            wsStatus={wsStatus}
            onRefresh={refresh}
            pendingResetsCount={pendingResetsCount}
            onReports={() => setShowReports(true)}
            onUsers={() => setShowUsers(true)}
            onSales={() => setShowSales(true)}
            onDrivers={() => setShowDrivers(true)}
            onPricing={() => setShowPricing(true)}
            onApprovals={() => setShowApprovals(true)}
            onCards={() => setShowCards(true)}
            onPwResets={() => setShowPwResets(true)}
            onSupport={() => setShowSupport(true)}
            onDuplicates={() => setShowDuplicates(true)}
            onMergeLog={() => setShowMergeLog(true)}
            onDriverWallets={() => setShowDriverWallets(true)}
            onProfile={() => setShowProfile(true)}
            onTeam={() => setShowTeam(true)}
            onCancelRequests={() => setShowCancels(true)}
            onBroadcast={() => setShowBroadcast(true)}
            onCashouts={() => setShowCashouts(true)}
            onTwilio={() => setShowTwilio(true)}
            onBeta={() => setShowBeta(true)}
            onBanners={() => setShowBanners(true)}
            onCategorySettings={() => setShowCatSettings(true)}
            pendingCashoutsCount={pendingCashoutsCount}
            pendingCancelCount={pendingCancelCount}
          />
        </div>
      </div>


      {showPricing && <DeliveryPricingModal onClose={() => setShowPricing(false)}/>}
      {showSales && <SalesModal onClose={() => setShowSales(false)}/>}
      {showReports && <ReportsPanel scope="admin" onClose={() => setShowReports(false)}/>}
      {showUsers && <AdminUsersModal onClose={() => setShowUsers(false)} currentAdminId={user?.id}/>}
      {showApprovals && <AdminApprovalsModal onClose={() => setShowApprovals(false)}/>}
      {showCards && <AdminCardsModal onClose={() => setShowCards(false)}/>}
      {showPwResets && <PasswordResetRequestsModal onClose={() => { setShowPwResets(false); refresh(); }}/>}
      {showSupport && <SupportContactsModal onClose={() => setShowSupport(false)}/>}
      {showDuplicates && <DuplicatesModal onClose={() => setShowDuplicates(false)}/>}
      {showMergeLog && <MergeLogModal onClose={() => setShowMergeLog(false)}/>}
      {showDriverWallets && <DriverWalletsModal onClose={() => setShowDriverWallets(false)}/>}
      {showProfile && <AdminProfileModal onClose={() => setShowProfile(false)}/>}
      {showTeam && <AdminTeamModal onClose={() => setShowTeam(false)}/>}
      {showCancels && <CancelRequestsModal onClose={() => { setShowCancels(false); refresh(); }}/>}
      {showBroadcast && <AdminBroadcastModal onClose={() => setShowBroadcast(false)}/>}
      {showCashouts && <AdminCashoutsModal onClose={() => setShowCashouts(false)} onChange={() => loadCashoutsCount()}/>}
      {showTwilio && <TwilioSettingsModal onClose={() => setShowTwilio(false)}/>}
      {showBeta && <AdminBetaTestersModal onClose={() => setShowBeta(false)}/>}
      {showBanners && <AdminBannersModal onClose={() => setShowBanners(false)}/>}
      {showCatSettings && <AdminCategorySettingsModal onClose={() => setShowCatSettings(false)}/>}
      {showDrivers && <DriversModal onClose={() => setShowDrivers(false)}/>}
      {showClearRevenue && (
        <ClearRevenueModal
          busy={clearBusy}
          result={clearResult}
          onClose={() => { setShowClearRevenue(false); setClearResult(null); }}
          onConfirm={async (options) => {
            setClearBusy(true);
            try {
              const { data } = await api.post("/admin/orders/clear-completed", options);
              setClearResult(data);
              refresh();
            } catch (e) {
              setClearResult({ error: extractError(e) });
            } finally { setClearBusy(false); }
          }}
        />
      )}
    </div>
  );
}

function SalesModal({ onClose }) {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true); setErr("");
      try {
        const { data } = await api.get(`/admin/analytics/daily?days=${days}`);
        setData(data);
      } catch (e) { setErr(extractError(e)); }
      finally { setLoading(false); }
    })();
  }, [days]);

  const maxRevenue = data?.daily?.reduce((m, d) => Math.max(m, d.revenue), 0) || 1;
  const dayLabel = (iso) => {
    try { return new Date(iso).toLocaleDateString("ar-LY", {weekday: "short", day: "2-digit", month: "2-digit"}); }
    catch { return iso; }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose} data-testid="sales-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-2xl rounded-3xl shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white flex justify-between items-center p-5 border-b border-[var(--border)] z-10">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-primary-brand text-white flex items-center justify-center"><BarChart3 size={18}/></div>
            <div>
              <h2 className="text-lg font-black" style={{fontFamily:'Cairo'}}>المبيعات اليومية</h2>
              <div className="text-[11px] text-[var(--text-secondary)]">إحصائيات الطلبات والإيرادات اليومية</div>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-[var(--surface-alt)] flex items-center justify-center"><X size={16}/></button>
        </div>
        <div className="p-5">
          <div className="flex gap-2 mb-4">
            {[7, 14, 30].map(d => (
              <button key={d} onClick={() => setDays(d)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold ${days === d ? "bg-primary-brand text-white" : "bg-[var(--surface-alt)] text-[var(--text-primary)]"}`}
                data-testid={`sales-range-${d}`}>
                آخر {d} يوم
              </button>
            ))}
          </div>

          {err && <div className="bg-red-50 text-red-600 px-3 py-2 rounded-lg text-sm mb-3">{err}</div>}

          {loading ? (
            <div className="text-sm text-[var(--text-secondary)] text-center py-6">جاري التحميل…</div>
          ) : data ? (
            <>
              {/* Totals */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                <div className="bg-primary-light rounded-xl p-3">
                  <div className="text-[10px] text-primary-brand font-bold">إجمالي الطلبات</div>
                  <div className="text-2xl font-black text-primary-brand" style={{fontFamily:'Cairo'}} data-testid="sales-total-orders">{data.totals.orders_count}</div>
                </div>
                <div className="bg-green-50 rounded-xl p-3">
                  <div className="text-[10px] text-green-700 font-bold">تم التسليم</div>
                  <div className="text-2xl font-black text-green-700" style={{fontFamily:'Cairo'}} data-testid="sales-delivered">{data.totals.delivered_count}</div>
                </div>
                <div className="bg-blue-50 rounded-xl p-3">
                  <div className="text-[10px] text-blue-700 font-bold">الإيرادات (د.ل)</div>
                  <div className="text-2xl font-black text-blue-700" style={{fontFamily:'Cairo'}} data-testid="sales-revenue">{data.totals.revenue.toFixed(0)}</div>
                </div>
                <div className="bg-blue-50 rounded-xl p-3">
                  <div className="text-[10px] text-blue-700 font-bold">رسوم التوصيل</div>
                  <div className="text-2xl font-black text-blue-700" style={{fontFamily:'Cairo'}} data-testid="sales-delivery-fees">{data.totals.delivery_fees.toFixed(0)}</div>
                </div>
              </div>

              {/* Daily bar chart */}
              <div className="bg-[var(--surface-alt)] rounded-2xl p-3 mb-4">
                <div className="text-xs font-bold text-primary-brand mb-3 flex items-center gap-1"><TrendingUp size={12}/> الإيرادات اليومية</div>
                <div className="flex items-end gap-1 h-40" data-testid="sales-chart">
                  {data.daily.map((d) => (
                    <div key={d.date} className="flex-1 flex flex-col items-center gap-1 min-w-0" title={`${d.date} · ${d.orders_count} طلب · ${d.revenue} د.ل`}>
                      <div className="text-[9px] font-bold text-primary-brand">{d.revenue > 0 ? d.revenue.toFixed(0) : ""}</div>
                      <div className="w-full bg-primary-brand rounded-t transition-all"
                        style={{ height: `${Math.max(4, (d.revenue / maxRevenue) * 100)}%` }}
                        data-testid={`bar-${d.date}`}/>
                      <div className="text-[9px] text-[var(--text-secondary)] truncate w-full text-center">{dayLabel(d.date)}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top restaurants */}
              {data.top_restaurants.length > 0 && (
                <div className="bg-white border border-[var(--border)] rounded-2xl p-3">
                  <div className="text-xs font-bold text-primary-brand mb-2">أعلى المطاعم إيراداً</div>
                  <div className="flex flex-col gap-1.5" data-testid="top-restaurants">
                    {data.top_restaurants.map((r, i) => (
                      <div key={r.restaurant_id} className="flex items-center gap-2 text-sm">
                        <span className="w-5 h-5 rounded-full bg-primary-brand text-white text-[10px] flex items-center justify-center font-bold">{i + 1}</span>
                        <span className="flex-1 truncate font-bold">{r.name || "بدون اسم"}</span>
                        <span className="text-xs text-[var(--text-secondary)]">{r.orders} طلب</span>
                        <span className="text-sm font-black text-primary-brand" style={{fontFamily:'Cairo'}}>{r.revenue.toFixed(0)} د.ل</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function DriversModal({ onClose }) {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [invite, setInvite] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/admin/drivers");
      setDrivers(data);
    } catch (e) { setErr(extractError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (d) => {
    if (!window.confirm(`حذف السائق ${d.name || d.email} نهائياً؟ سيتم إلغاء ارتباطه بجميع طلباته السابقة.`)) return;
    setBusy(true); setErr("");
    try {
      await api.delete(`/admin/drivers/${d.user_id}`);
      await load();
    } catch (e) { setErr(extractError(e)); }
    finally { setBusy(false); }
  };

  const genInvite = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/admin/driver-invites");
      setInvite(data);
    } catch (e) { setErr(extractError(e)); }
    finally { setBusy(false); }
  };

  const copyInvite = async () => {
    if (!invite) return;
    const url = `${window.location.origin}${invite.invite_url}`;
    try { await navigator.clipboard.writeText(url); window.alert("تم نسخ رابط دعوة السائق"); }
    catch { window.prompt("انسخ الرابط:", url); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose} data-testid="drivers-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-2xl rounded-3xl shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white flex justify-between items-center p-5 border-b border-[var(--border)] z-10">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-primary-brand text-white flex items-center justify-center"><Bike size={18}/></div>
            <div>
              <h2 className="text-lg font-black" style={{fontFamily:'Cairo'}}>السائقون ({drivers.length})</h2>
              <div className="text-[11px] text-[var(--text-secondary)]">إدارة السائقين وإحصائياتهم</div>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-[var(--surface-alt)] flex items-center justify-center"><X size={16}/></button>
        </div>
        <div className="p-5">
          {err && <div className="bg-red-50 text-red-600 px-3 py-2 rounded-lg text-sm mb-3">{err}</div>}
          <div className="flex flex-wrap gap-2 mb-4">
            <button onClick={genInvite} disabled={busy} className="px-4 py-2 rounded-xl bg-primary-brand text-white font-bold text-sm flex items-center gap-2 disabled:opacity-60" data-testid="btn-gen-driver-invite">
              <UserCheck size={14}/> توليد كود دعوة سائق
            </button>
          </div>
          {invite && (
            <div className="bg-amber-50 border border-amber-300 rounded-xl p-3 mb-3" data-testid="driver-invite-box">
              <div className="text-xs text-amber-800 mb-1">شارك هذا الرابط مع السائق للتسجيل (استخدام مرة واحدة):</div>
              <div className="flex items-center gap-2">
                <div className="flex-1 font-mono text-xs bg-white rounded-lg px-3 py-2 border border-amber-200 truncate" dir="ltr">
                  {window.location.origin}{invite.invite_url}
                </div>
                <button onClick={copyInvite} className="p-2 rounded-lg bg-amber-600 text-white" data-testid="btn-copy-driver-invite">نسخ</button>
              </div>
              <div className="text-[10px] text-amber-700 mt-1">الكود: <span className="font-mono">{invite.code}</span></div>
            </div>
          )}
          {loading ? (
            <div className="text-sm text-[var(--text-secondary)] text-center py-6">جاري التحميل…</div>
          ) : drivers.length === 0 ? (
            <div className="text-center py-10 text-sm text-[var(--text-secondary)]">لا يوجد سائقون مسجّلون</div>
          ) : (
            <div className="flex flex-col gap-2">
              {drivers.map(d => {
                const statusColor = d.status === "available" ? "bg-green-100 text-green-700"
                  : d.status === "busy" ? "bg-blue-100 text-blue-700"
                  : "bg-gray-100 text-gray-600";
                const statusLabel = d.status === "available" ? "متاح" : d.status === "busy" ? "مشغول" : "غير متصل";
                return (
                <div key={d.id} className="bg-white border border-[var(--border)] rounded-xl p-3" data-testid={`driver-row-${d.user_id}`}>
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <div className="font-bold text-sm truncate">{d.name || "بدون اسم"}</div>
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${statusColor}`}>{statusLabel}</span>
                      </div>
                      <div className="text-xs text-[var(--text-secondary)] truncate" dir="ltr">{d.email || "—"}</div>
                      <div className="text-[10px] text-[var(--text-secondary)] mt-1">{d.phone || "—"} · {d.vehicle}</div>
                    </div>
                    <button onClick={() => remove(d)} disabled={busy} title="حذف السائق نهائياً"
                      className="w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 text-red-500 flex items-center justify-center disabled:opacity-60"
                      data-testid={`del-driver-${d.user_id}`}>
                      <Trash2 size={14}/>
                    </button>
                  </div>
                  <div className="flex gap-3 mt-2 pt-2 border-t border-[var(--border)] text-xs">
                    <div className="flex-1"><span className="text-[var(--text-secondary)]">تسليمات: </span><span className="font-bold" data-testid={`driver-deliveries-${d.user_id}`}>{d.deliveries_count ?? 0}</span></div>
                    <div className="flex-1"><span className="text-[var(--text-secondary)]">الرصيد: </span><span className="font-black text-primary-brand" style={{fontFamily:'Cairo'}} data-testid={`driver-payout-${d.user_id}`}>{(d.total_payout ?? 0).toFixed(2)} د.ل</span></div>
                    <div className="flex-1"><span className="text-[var(--text-secondary)]">التقييم: </span><span className="font-bold">{d.rating?.toFixed(1) || "—"}</span></div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DeliveryPricingModal({ onClose }) {
  const [form, setForm] = useState({ base_fee: 5, base_km: 5, per_km: 1, platform_commission: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings/delivery-pricing");
        setForm({
          base_fee: data.base_fee,
          base_km: data.base_km,
          per_km: data.per_km,
          platform_commission: data.platform_commission ?? 0,
        });
      } catch (e) { setErr(extractError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    if (form.base_fee <= 0 || form.base_km <= 0 || form.per_km < 0 || form.platform_commission < 0) {
      setErr("القيم يجب أن تكون موجبة"); return;
    }
    if (form.platform_commission > form.base_fee) {
      setErr("عمولة التطبيق يجب ألا تتجاوز الحد الأدنى للتوصيل"); return;
    }
    setBusy(true); setErr(""); setOk("");
    try {
      await api.patch("/admin/settings/delivery-pricing", {
        base_fee: parseFloat(form.base_fee),
        base_km: parseFloat(form.base_km),
        per_km: parseFloat(form.per_km),
        platform_commission: parseFloat(form.platform_commission),
      });
      setOk("تم حفظ الأسعار — سيتم تطبيقها فوراً على جميع الطلبات الجديدة");
      setTimeout(onClose, 1500);
    } catch (e) { setErr(extractError(e)); }
    finally { setBusy(false); }
  };

  const calcExampleFee = (km) => {
    const extra = Math.max(0, Math.ceil(km - form.base_km));
    return (form.base_fee + extra * form.per_km).toFixed(2);
  };
  const example5km = calcExampleFee(5);
  const example10km = calcExampleFee(10);
  const example15km = calcExampleFee(15);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose} data-testid="pricing-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-md rounded-3xl shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white flex justify-between items-center p-5 border-b border-[var(--border)] z-10">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-primary-brand text-white flex items-center justify-center"><Wallet size={18}/></div>
            <div>
              <h2 className="text-lg font-black" style={{fontFamily:'Cairo'}}>أسعار التوصيل</h2>
              <div className="text-[11px] text-[var(--text-secondary)]">تُطبَّق فوراً على كل الطلبات الجديدة</div>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-[var(--surface-alt)] flex items-center justify-center"><X size={16}/></button>
        </div>
        <div className="p-5">
          {loading ? (
            <div className="text-sm text-[var(--text-secondary)] text-center py-6">جاري التحميل…</div>
          ) : (
            <>
              <div className="flex flex-col gap-3">
                <div>
                  <label className="text-sm font-bold block mb-1">السعر الأساسي (د.ل)</label>
                  <input type="number" step="0.5" min="0.5" value={form.base_fee}
                    onChange={(e) => setForm({...form, base_fee: parseFloat(e.target.value) || 0})}
                    className="w-full px-4 py-2.5 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none text-right"
                    data-testid="pricing-base-fee"/>
                  <div className="text-[11px] text-[var(--text-secondary)] mt-1">يُدفع مقابل التوصيل ضمن النطاق الأساسي</div>
                </div>
                <div>
                  <label className="text-sm font-bold block mb-1">نطاق السعر الأساسي (كم)</label>
                  <input type="number" step="0.5" min="0.5" value={form.base_km}
                    onChange={(e) => setForm({...form, base_km: parseFloat(e.target.value) || 0})}
                    className="w-full px-4 py-2.5 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none text-right"
                    data-testid="pricing-base-km"/>
                  <div className="text-[11px] text-[var(--text-secondary)] mt-1">أي مسافة أقل أو تساوي هذا الحد → السعر الأساسي</div>
                </div>
                <div>
                  <label className="text-sm font-bold block mb-1">سعر كل كيلومتر إضافي (د.ل/كم)</label>
                  <input type="number" step="0.25" min="0" value={form.per_km}
                    onChange={(e) => setForm({...form, per_km: parseFloat(e.target.value) || 0})}
                    className="w-full px-4 py-2.5 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none text-right"
                    data-testid="pricing-per-km"/>
                  <div className="text-[11px] text-[var(--text-secondary)] mt-1">يُضاف عن كل كم فوق النطاق (يُقرَّب لأعلى)</div>
                </div>
                <div className="col-span-full">
                  <label className="text-sm font-bold block mb-1 flex items-center gap-1">
                    عمولة التطبيق من كل توصيل (د.ل)
                    <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-black">جديد</span>
                  </label>
                  <input type="number" step="0.25" min="0" value={form.platform_commission}
                    onChange={(e) => setForm({...form, platform_commission: parseFloat(e.target.value) || 0})}
                    className="w-full px-4 py-2.5 rounded-xl border border-emerald-300 bg-emerald-50 focus:bg-white focus:border-emerald-500 focus:outline-none text-right font-black"
                    data-testid="pricing-platform-commission"/>
                  <div className="text-[11px] text-[var(--text-secondary)] mt-1">
                    مبلغ ثابت يُخصم من رسوم التوصيل ويُضاف إلى إيرادات التطبيق. السائق يستلم الباقي.
                    <br/>
                    <b>مثال:</b> توصيل {form.base_fee} د.ل + عمولة {form.platform_commission} د.ل → السائق يستلم {(form.base_fee - form.platform_commission).toFixed(2)} د.ل، التطبيق {parseFloat(form.platform_commission || 0).toFixed(2)} د.ل
                  </div>
                </div>
              </div>

              <div className="mt-4 bg-[var(--surface-alt)] rounded-2xl p-3">
                <div className="text-xs font-bold text-primary-brand mb-2">أمثلة حيّة على الحساب</div>
                <div className="grid grid-cols-3 gap-2 text-center text-xs">
                  <div className="bg-white rounded-lg p-2">
                    <div className="text-[10px] text-[var(--text-secondary)]">5 كم</div>
                    <div className="font-black text-primary-brand" data-testid="ex-5km">{example5km}</div>
                    <div className="text-[10px]">د.ل</div>
                  </div>
                  <div className="bg-white rounded-lg p-2">
                    <div className="text-[10px] text-[var(--text-secondary)]">10 كم</div>
                    <div className="font-black text-primary-brand" data-testid="ex-10km">{example10km}</div>
                    <div className="text-[10px]">د.ل</div>
                  </div>
                  <div className="bg-white rounded-lg p-2">
                    <div className="text-[10px] text-[var(--text-secondary)]">15 كم</div>
                    <div className="font-black text-primary-brand" data-testid="ex-15km">{example15km}</div>
                    <div className="text-[10px]">د.ل</div>
                  </div>
                </div>
              </div>

              {err && <div className="mt-3 bg-red-50 text-red-600 px-3 py-2 rounded-lg text-sm">{err}</div>}
              {ok && <div className="mt-3 bg-green-50 text-green-700 px-3 py-2 rounded-lg text-sm" data-testid="pricing-ok">{ok}</div>}

              <div className="flex gap-2 mt-4">
                <button onClick={onClose} className="flex-1 py-3 rounded-xl bg-[var(--surface-alt)] font-bold text-sm">إلغاء</button>
                <button onClick={save} disabled={busy}
                  className="flex-[2] py-3 rounded-xl bg-primary-brand text-white font-bold text-sm talabya-shadow-float disabled:opacity-60 flex items-center justify-center gap-2"
                  data-testid="pricing-save">
                  <Check size={16}/> {busy ? "جاري الحفظ..." : "حفظ الأسعار"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ClearRevenueModal({ busy, result, onClose, onConfirm }) {
  // Options mirror the backend ClearCompletedIn model. `cancelled_orders`
  // starts unchecked — the audit trail is usually worth preserving.
  const [opts, setOpts] = useState({
    orders: true,
    cancelled_orders: false,
    chats: true,
    voice_notes: true,
    call_logs: true,
    call_recordings: true,
    loyalty: true,
    wallet_txns: true,
    driver_counters: true,
  });
  const [confirmed, setConfirmed] = useState(false);
  const done = !!result && !result.error;
  const nothingChosen = Object.values(opts).every((v) => !v);

  const OPTION_META = [
    { key: "orders",           label: "الطلبات المكتملة (المُسلَّمة)",       hint: "مصدر الإيرادات — رئيسي" },
    { key: "cancelled_orders", label: "الطلبات الملغاة",                     hint: "افتراضياً محفوظة للتدقيق" },
    { key: "chats",            label: "محادثات الطلبات",                    hint: "رسائل الشات بين الأطراف" },
    { key: "voice_notes",      label: "الرسائل الصوتية",                    hint: "voice_notes المرتبطة بالطلبات" },
    { key: "call_logs",        label: "سجلات المكالمات",                    hint: "بانرات المكالمات الفائتة" },
    { key: "call_recordings",  label: "تسجيلات المكالمات",                  hint: "التسجيلات الصامتة للأدمن" },
    { key: "loyalty",          label: "نقاط الولاء المكتسبة",               hint: "loyalty_ledger من هذه الطلبات" },
    { key: "wallet_txns",      label: "معاملات المحفظة المرتبطة",           hint: "مدفوعات وخصومات على الطلبات" },
    { key: "driver_counters",  label: "تصفير عدّادات إيرادات السائقين",     hint: "earnings_today/total = 0" },
  ];

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" data-testid="clear-revenue-modal">
      <div className="bg-white rounded-3xl w-full max-w-lg p-6 talabya-shadow-float max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-3 sticky top-0 bg-white -mt-2 pt-2">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-red-100 text-red-600 flex items-center justify-center">
              <Trash2 size={18}/>
            </div>
            <div>
              <div className="font-black text-lg" style={{fontFamily:'Cairo'}}>مسح إيرادات مكتملة</div>
              <div className="text-xs text-[var(--text-secondary)]">اختر ما تريد حذفه بالضبط</div>
            </div>
          </div>
          <button onClick={onClose} disabled={busy} className="text-[var(--text-secondary)] hover:text-red-600 p-1" data-testid="clear-modal-close">
            <X size={18}/>
          </button>
        </div>

        {!done && !result?.error && (
          <div className="mt-2 space-y-3">
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-2.5 text-[11px] text-emerald-800">
              <b>محمي دوماً:</b> الطلبات الجارية · المستخدمون · المطاعم · القوائم · الإعدادات
            </div>

            <div className="flex items-center gap-2 text-xs">
              <button
                type="button"
                onClick={() => setOpts(Object.fromEntries(OPTION_META.map((o) => [o.key, true])))}
                className="px-2 py-1 rounded-lg bg-primary-light text-primary-brand font-black hover:opacity-80"
                data-testid="select-all-btn">تحديد الكل</button>
              <button
                type="button"
                onClick={() => setOpts(Object.fromEntries(OPTION_META.map((o) => [o.key, false])))}
                className="px-2 py-1 rounded-lg bg-white border border-[var(--border)] font-bold hover:bg-[var(--surface-alt)]"
                data-testid="select-none-btn">إلغاء الكل</button>
            </div>

            <div className="border border-[var(--border)] rounded-2xl divide-y divide-[var(--border)]">
              {OPTION_META.map((o) => (
                <label key={o.key}
                  className="flex items-start gap-3 p-3 cursor-pointer hover:bg-[var(--surface-alt)] transition"
                  data-testid={`opt-row-${o.key}`}
                >
                  <input
                    type="checkbox"
                    checked={!!opts[o.key]}
                    onChange={(e) => setOpts({ ...opts, [o.key]: e.target.checked })}
                    className="mt-0.5 w-4 h-4 accent-primary-brand"
                    data-testid={`opt-${o.key}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm">{o.label}</div>
                    <div className="text-[11px] text-[var(--text-secondary)] mt-0.5">{o.hint}</div>
                  </div>
                </label>
              ))}
            </div>

            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} data-testid="clear-confirm-checkbox"/>
              <span>أفهم أن هذا الإجراء نهائي، وأريد الاستمرار.</span>
            </label>

            <div className="flex gap-2 pt-1">
              <button onClick={onClose} disabled={busy} className="flex-1 py-3 rounded-xl bg-white border border-[var(--border)] font-bold disabled:opacity-60" data-testid="clear-cancel">
                إلغاء
              </button>
              <button
                onClick={() => onConfirm(opts)}
                disabled={busy || !confirmed || nothingChosen}
                className="flex-1 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-black disabled:opacity-50"
                data-testid="clear-confirm"
              >
                {busy ? "جاري المسح..." : nothingChosen ? "اختر عنصراً واحداً على الأقل" : "تأكيد المسح"}
              </button>
            </div>
          </div>
        )}

        {done && (
          <div className="mt-2 space-y-2" data-testid="clear-result">
            <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-3">
              <div className="flex items-center gap-2 text-emerald-800 font-black mb-2">
                <Check size={18}/> تم المسح بنجاح
              </div>
              <ul className="text-xs text-emerald-900 space-y-0.5">
                <li>الطلبات المكتملة المحذوفة: <b>{result.delivered_orders_deleted}</b></li>
                <li>الطلبات الملغاة المحذوفة: <b>{result.cancelled_orders_deleted}</b></li>
                <li>محادثات محذوفة: <b>{result.order_messages_deleted}</b></li>
                <li>رسائل صوتية محذوفة: <b>{result.voice_notes_deleted}</b></li>
                <li>سجلات مكالمات محذوفة: <b>{result.call_logs_deleted}</b></li>
                <li>تسجيلات مكالمات محذوفة: <b>{result.call_recordings_deleted}</b></li>
                <li>نقاط ولاء محذوفة: <b>{result.loyalty_ledger_deleted}</b></li>
                <li>معاملات محفظة محذوفة: <b>{result.wallet_transactions_deleted}</b></li>
                <li>سائقون تم تصفير عدّاداتهم: <b>{result.drivers_reset}</b></li>
              </ul>
            </div>
            <button onClick={onClose} className="w-full py-3 rounded-xl bg-primary-brand text-white font-black" data-testid="clear-done">
              حسناً
            </button>
          </div>
        )}

        {result?.error && (
          <div className="mt-2 rounded-2xl bg-red-50 border border-red-200 p-3 text-sm text-red-700" data-testid="clear-error">
            {result.error}
          </div>
        )}
      </div>
    </div>
  );
}

